import type { VariantInfo, Campaign, IBaseCampaignVersion } from '~/modules/campaign/types';
import type { ICGTemplateVariantProperty } from '../types';

import {
  useMetricsTimeFilter,
  findVersionByAlias,
  extractVersionSettings,
} from '~/modules/analytics';
import { CGTemplatePairVariantCell } from './CGTemplatePairVariantCell';
import { GTable } from '~/presentation/ui';
import { useMemo } from 'react';

interface CGTemplatePairVariantColumnProps {
  campaign: Campaign;
  variants: VariantInfo[];
  property: ICGTemplateVariantProperty;
  columnIndex: number;
  parsePaddingRow: (index: number) => any;
}

const PROPERTY_IGNORE = ['startTime', 'status', 'action'];

export const CGTemplatePairVariantColumn: React.FC<CGTemplatePairVariantColumnProps> = ({
  campaign,
  variants,
  property,
  columnIndex,
  parsePaddingRow,
}) => {
  const { currentDates } = useMetricsTimeFilter();

  const versionSetting = useMemo(() => {
    const version = findVersionByAlias(
      campaign.versions as IBaseCampaignVersion[],
      currentDates.alias,
    );

    return extractVersionSettings(version);
  }, [campaign.versions, currentDates.alias]);

  const isPropertyIgnored = PROPERTY_IGNORE.includes(property);
  const isFirstPropertyIgnored = property === PROPERTY_IGNORE[0];

  return (
    <GTable.Cell
      index={columnIndex}
      borders={{
        ...(isPropertyIgnored &&
          isFirstPropertyIgnored && {
            borderInlineStartWidth: '025',
            borderColor: 'border-brand',
          }),
      }}
    >
      {variants.map((variant, variantIndex) => (
        <CGTemplatePairVariantCell
          key={`variant-cell-${variant.id}`}
          variant={variant}
          property={property}
          campaign={campaign}
          variantIndex={variantIndex}
          columnIndex={columnIndex}
          parsePaddingRow={parsePaddingRow}
          isPropertyIgnored={isPropertyIgnored}
          campaignVariants={versionSetting?.campaignVariants}
        />
      ))}
    </GTable.Cell>
  );
};
